/**
 * Final-evaluation passport of quiz badges.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";

import { mapSectionToGradeDetails } from 'utils/grade.util';
import { getQuizModuleLinks } from "utils/course";
import { VButton } from "form";
import { useAsterController } from "context";
import { getModuleIndex } from "utils/string";
import { BBadge } from "components";

/**
 * Lists quiz badges required before the final exam can be taken.
 *
 * @param {{ onContinue?: Function }} props
 */
const FinalPassport = (props) => {

  const { onContinueClick, totalCount, cutoff } = props;

  const { courseId } = useParams();
  const { t } = useTranslation('common');

  const [controller] = useAsterController();
  const { courses, structure: courseStructure, preview } = controller;

  const [moduleGrades, setModuleGrades] = useState([]);
  const [notPassed, setNotPassed] = useState([]);
  const [isModuleComplete, setIsModuleComplete] = useState(false);

  useEffect(() => {
    const mapper = mapSectionToGradeDetails((courses.data || []).find(c => c.id === courseId));
    const quizes = mapper.filter(m => m['gradeFormat'] && m['gradeFormat'].toLowerCase() === 'quiz');

    const correctCount = quizes.filter(m => m.is_passed).length;
    if (correctCount === quizes.length) {
      setIsModuleComplete(true);
    }

    setModuleGrades(quizes);
    setNotPassed(quizes.filter(m => !m.is_passed).map(m => m['sectionTitle'].slice(-1)));
  }, [courses, courseId]);

  /**
   * Renders badge artwork for each quiz module.
   *
   * @returns {JSX.Element}
   */
  const renderBadges = () => {
    return moduleGrades.map(m => {
      const moduleIndex = getModuleIndex(m['sectionTitle']);

      if (moduleIndex)
        return <div className={m.is_passed ? '' : 'v-filter-gray'} key={m['sectionTitle']}>
          <BBadge index={Number(moduleIndex)} className="w-28 m-2" />
        </div>;
      else
        return <></>;
    });
  };

  /**
   * Links to quiz modules that are not yet passed.
   *
   * @returns {JSX.Element}
   */
  const renderModuleLinks = () => {
    if (Object.keys(courseStructure).length) {
      const candidateLinks = getQuizModuleLinks(courseStructure);
      return moduleGrades.filter(m => !m.is_passed).map(m => {
        const link = candidateLinks.find(ca => ca['title'] === m.sectionTitle)['route'];
        return <div key={m.sectionTitle}>
          <Link to={`/${preview ? 'preview' : 'content'}/${courseId}/${link}`} className="no-underline">
            <VButton>
              <Icon icon="akar-icons:chevron-right" />
              {t('Access the quiz of')}&nbsp;{m.sectionTitle}
            </VButton>
          </Link>
        </div>;
      });
    } else {
      return <></>;
    }

  };

  /**
   * Formats names of quizzes that are still incomplete.
   *
   * @returns {string}
   */
  const renderNotPassedNames = () => {
    if (notPassed.length === 0) {
      return <></>;
    } else if (notPassed.length === 1) {
      return <p className="font-bold fsp-18 text-dark">
        {t('You still have to pass the quiz of module')}&nbsp;
        {notPassed[0]}.</p>;
    } else if (notPassed.length === 2) {
      return <p className="font-bold fsp-18 text-dark">
        {t('You still have to pass the quiz of module')}&nbsp;
        {notPassed[0]} and {notPassed[1]}.
      </p>;
    } else {
      const former = notPassed.slice(0, notPassed.length - 2);
      return <p className="font-bold fsp-18 text-dark">
        {t('You still have to pass the quiz of module')}&nbsp;
        {former.map(f => f + ',')}&nbsp;
        {notPassed[notPassed.length - 2]}&nbsp;
        and {notPassed[notPassed.length - 1]}.
      </p>;
    }
  };

  return <>
    <div className="w-full shadow p-4">
      <div className="flex items-center flex-col mt-5">
        <div className="flex flex-wrap">
          {renderBadges()}
        </div>
      </div>
      {
        !isModuleComplete ?
          <div className="mt-4">
            <p className="font-bold fsp-22 text-dark">
              {t('Your badge collection is incomplete')}
            </p>
            {renderNotPassedNames()}
            <p className=" fsp-18 text-muted text-dark">
              {t('Once you have passed all the quizzes, you will be able to access the final exam and finalize your training.')}
            </p>
            {renderModuleLinks()}
          </div>
          :
          <div className="mt-4 text-center">
            <p className="font-bold fsp-22 text-dark">
              {t('Congratulation! Your badge collection is complete.')}
            </p>
            <p className="text-base">
              {t('To validate your final assessment, you need')}&nbsp;
              {Math.round(cutoff * 100)}%&nbsp;
              {t('correct answers')}&nbsp;
              ({Math.ceil(cutoff * totalCount)}&nbsp;
              {t('correct answers out of')}&nbsp;
              {totalCount})</p>
            <VButton onClick={onContinueClick} variant="contained">
              {t('Start the final assessment')}
            </VButton>
            <ul className="mt-4 text-left">
              <li>{t("You can start over as many times as you want. We promise, we'll only keep the best score.")}</li>
              <li>{t("Once your final assessment has been validated, your certificate of achievement will be available to you in your documents.")}</li>
            </ul>
          </div>

      }
    </div>

  </>;
};

export default FinalPassport;